import { FC, useRef, useState, useMemo } from 'react';
import { InputProps } from './input.types';
import Picker from 'react-date-picker';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import './datePickerStacking.css';
import { format, parse } from 'date-fns';
import { LooseValue } from 'react-calendar/dist/shared/types';
import { ArrowDownIcon, ArrowUpIcon, CalendarDotsIcon } from '@phosphor-icons/react';
import { useInteractOutside } from 'react-aria';
import { useIntl } from 'react-intl';
import { getDateFnsLocale } from 'src/utils/getDateFnsLocale';

const DatePicker: FC<InputProps> = ({ disabled, min, max, value, onChange, name, 'data-testid': dataTest }) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { locale } = useIntl();
  const dateFnsLocale = useMemo(() => getDateFnsLocale(locale), [locale]);

  const pickerLocale = useMemo(() => {
    // react-date-picker expects a BCP-47 string, normalize underscore variants
    return locale ? locale.replace('_', '-') : 'en';
  }, [locale]);

  const handleChange = (date: LooseValue) => {
    const value = date ? format(date as Date, 'yyyy-MM-dd') : null;

    onChange?.({ target: { name, value } } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleClickOutside = () => {
    if (calendarOpen) {
      setCalendarOpen(false);
    }
  };

  useInteractOutside({
    ref: ref,
    onInteractOutside: handleClickOutside,
  });

  return (
    <div ref={ref} style={{ flexGrow: 1, display: 'flex' }} onClick={() => !disabled && setCalendarOpen(true)}>
      <Picker
        isOpen={calendarOpen}
        onCalendarOpen={() => {
          setCalendarOpen(true);
        }}
        onCalendarClose={() => {
          setCalendarOpen(false);
        }}
        disabled={disabled}
        minDate={min ? parse(min as string, 'yyyy-MM-dd', new Date()) : undefined}
        maxDate={max ? parse(max as string, 'yyyy-MM-dd', new Date()) : undefined}
        value={value ? parse(value as string, 'yyyy-MM-dd', new Date()) : null}
        onChange={handleChange}
        calendarProps={{
          formatShortWeekday: (_locale, date) => format(date, 'EEEEEE', { locale: dateFnsLocale }),
          prevLabel: <ArrowUpIcon size={16} weight="bold" />,
          nextLabel: <ArrowDownIcon size={16} weight="bold" />,
        }}
        shouldCloseCalendar={(e) => e.reason !== 'outsideAction'}
        clearIcon={null}
        calendarIcon={<CalendarDotsIcon size={20} />}
        format="dd/MM/yyyy"
        showLeadingZeros={false}
        data-testid={dataTest || 'date-picker-input'}
        locale={pickerLocale}
      />
    </div>
  );
};

export default DatePicker;
